import React from 'react';
import { t } from 'i18next';
import { useReactiveVar } from '@apollo/client';
import UploadDocumentButton from '@fingo/lib/components/buttons/UploadDocumentButton';
import DeleteDocumentButton from '@fingo/lib/components/buttons/DeleteDocumentButton';
import AmountWithIvaCell from '@fingo/lib/components/cells/AmountWithIvaCell';
import CompanyCell from '@fingo/lib/components/cells/CompanyCell';
import DateCell from '@fingo/lib/components/cells/DateCell';
import FolioCell from '@fingo/lib/components/cells/FolioCell';
import { FOLIO_CELL_CLASS_NAME } from '@fingo/lib/theme/constants/data-grid';
import TransferConditionsCell from '@fingo/lib/components/cells/TransferConditionsCell';
import TextCell from '@fingo/lib/components/cells/TextCell';
import { offerSelectedClientsVar } from '@fingo/lib/apollo/reactive-variables/shopping-cart';
import DateToPaySelectCell from '@fingo/lib/components/cells/DateToPaySelectCell';
import { ONBOARDING_LAST_TABLE_CELL_CLASS_NAME } from '@fingo/lib/theme/constants/table';
import HasRatificationCell from '@fingo/lib/components/cells/HasRatificationCell';
import DateToPaySelectionStateManagerCell from '@fingo/lib/components/cells/DateToPaySelectionStateManagerCell';
import SelectDaysButtonsSelectionStateManagerCell from '@fingo/lib/components/cells/SelectDaysButtonsSelectionStateManagerCell';

const DAYS_BUTTONS = [
  { days: 30, label: '30', id: '30-days' },
  { days: 60, label: '60', id: '60-days' },
  { days: 90, label: '90', id: '90-days' },
];

export const HEADERS = [
  'folio',
  'dteType_Code',
  'dateIssued',
  'receiver_sortable',
  'amountWithIva',
  'offer_pendingDocuments',
  'uploadDocuments',
];

export const MOBILE_HEADERS = [
  'folio',
  'receiver_sortable',
  'offer_pendingDocuments',
  'amountWithIva',
];

export const CLIENT_HEADERS = [
  'name',
  'invoicesCount',
  'dateToPayButtons',
];

export const DETAILS_HEADERS = [
  'folio',
  'dateIssued',
  'amountWithIva',
  'offer_DefaultRate',
  'dateToPay',
  'deleteDocument',
];

export const DETAILS_MOBILE_HEADERS = [
  'folio',
  'amountWithIva',
  'offer_DefaultRate',
  'dateToPay',
  'deleteDocument',
];

export const getColumns = (
  isMobile,
  handleDelete,
) => [
  {
    field: 'folio',
    headerName: 'Folio',
    disableColumnMenu: true,
    width: isMobile ? 65 : 105,
    cellClassName: FOLIO_CELL_CLASS_NAME,
    renderCell: ({ row }) => <FolioCell invoice={row} maxLength={6} />,
  },
  {
    field: 'dteType_Code',
    headerName: t('dte'),
    sortable: false,
    disableColumnMenu: true,
    width: 86,
    valueGetter: ({ row }) => row.dteType.name,
  },
  {
    field: 'receiver_sortable',
    headerName: 'Receptor',
    disableColumnMenu: true,
    flex: 1,
    minWidth: isMobile ? 115 : 90,
    renderCell: ({ row }) => <CompanyCell company={row.receiver} />,
  },
  {
    field: 'dateIssued',
    headerName: 'Emisión',
    disableColumnMenu: true,
    width: isMobile ? 85 : 124,
    renderCell: ({ row }) => <DateCell date={row.dateIssued} />,
  },
  {
    field: 'amountWithIva',
    headerName: 'Monto',
    disableColumnMenu: true,
    flex: handleDelete ? 1 : 0,
    minWidth: isMobile ? 90 : 126,
    renderCell: ({ row }) => <AmountWithIvaCell row={row} />,
  },
  {
    field: 'dateToPay',
    headerName: 'Fecha de pago',
    sortable: false,
    disableColumnMenu: true,
    width: isMobile ? 140 : 178,
    editable: true,
    renderEditCell: (params) => <DateToPaySelectionStateManagerCell params={params} />,
    renderCell: (params) => <DateToPaySelectionStateManagerCell params={params} />,
  },
  {
    field: 'offer_pendingDocuments',
    headerName: 'Estado',
    type: 'string',
    align: 'left',
    sortable: false,
    disableColumnMenu: true,
    width: isMobile ? 170 : 170,
    renderCell: ({ row }) => (
      <TransferConditionsCell invoice={row} type="Operations" />
    ),
  },
  {
    field: 'offerTransferCondition',
    headerName: 'Ratificación',
    type: 'string',
    sortable: true,
    disableColumnMenu: true,
    width: isMobile ? 90 : 136,
    renderCell: ({ row }) => <HasRatificationCell offer={row.offer} />,
  },
  {
    field: 'uploadDocuments',
    type: 'actions',
    width: isMobile ? 40 : 81,
    renderCell: ({ row }) => <UploadDocumentButton invoiceId={row.id} />,
  },
  {
    field: 'deleteDocument',
    headerName: 'Acción',
    type: 'actions',
    width: isMobile ? 40 : 55,
    sortable: false,
    cellClassName: 'no-padding-cell',
    headerClassName: 'no-padding-header',
    renderCell: ({ row }) => {
      if (!handleDelete) return null;
      return (
        <DeleteDocumentButton
          invoiceId={row.id}
          handleDelete={handleDelete}
          id="available-offers-delete-button"
        />
      );
    },
  },
];

export const getClientColumns = (
  selectedDaysButtonsOnChange,
  selectedDaysButtonsOnRecommendedPaymentTerm,
  isMobile,
  sameDateForAllSelection,
  minDaysFromToday,
) => [
  {
    field: 'name',
    headerName: 'Cliente',
    disableColumnMenu: true,
    flex: 1,
    maxWidth: 400,
    level: 0,
    renderCell: ({ row }) => <CompanyCell company={row} titleVariant="small_3" subtitleVariant="small_3" noWrap={false} />,
  },
  {
    field: 'invoicesCount',
    headerName: 'Facturas',
    disableColumnMenu: true,
    width: 88,
    level: 0,
    renderCell: ({ row }) => {
      const offerSelectedClients = useReactiveVar(offerSelectedClientsVar);
      const clientOffers = offerSelectedClients[row.id] || [];
      const invoicesCount = clientOffers.length;

      return (
        <TextCell
          text={`[${invoicesCount} factura${invoicesCount > 1 ? 's' : ''}]`}
          row={row}
          sx={{
            color: 'gray.A400',
            fontSize: '14px',
            fontWeight: 600,
          }}
        />
      );
    },
  },
  {
    field: 'dateToPayButtons',
    headerName: 'Días de plazo',
    disableColumnMenu: true,
    width: isMobile ? 250 : 280,
    level: 1,
    columnClassName: ONBOARDING_LAST_TABLE_CELL_CLASS_NAME,
    renderCell: (params) => (
      <SelectDaysButtonsSelectionStateManagerCell
        params={params}
        selectedDaysButtonsOnChange={selectedDaysButtonsOnChange}
        daysButtons={DAYS_BUTTONS}
        isMobile={isMobile}
        selectedDaysButtonsOnRecommendedPaymentTerm={
          selectedDaysButtonsOnRecommendedPaymentTerm
        }
        sameDateForAllSelection={sameDateForAllSelection}
        minDaysFromToday={minDaysFromToday}
      />
    ),
  },
  {
    field: 'dateToPaySelector',
    headerName: 'Fecha de pago',
    disableColumnMenu: true,
    width: 220,
    level: 1,
    renderCell: ({ row }) => (
      <DateToPaySelectCell
        row={row}
        selectedDaysButtonsOnChange={selectedDaysButtonsOnChange}
        selectedDaysButtonsOnRecommendedPaymentTerm={
          selectedDaysButtonsOnRecommendedPaymentTerm
        }
        sameDateForAllSelection={sameDateForAllSelection}
        minDaysFromToday={minDaysFromToday}
      />
    ),
  },
];
